import type { AssetMeta, ID } from '@/types';
import { uid } from '@/lib/utils';
import { blobStore } from './db';

export interface LoadedAsset {
  id: ID;
  url: string;
  el: HTMLImageElement;
  width: number;
  height: number;
}

const cache = new Map<ID, LoadedAsset>();
const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}

export const assetStore = {
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  getVersion: () => version,
  get: (id: ID | null | undefined) => (id ? cache.get(id) : undefined),
  has: (id: ID) => cache.has(id),
  ids: () => Array.from(cache.keys()),
};

export const ACCEPTED = 'image/png,image/jpeg,image/webp,image/gif,image/avif,image/heic,image/heif';

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = url;
  });
}

async function register(id: ID, blob: Blob): Promise<LoadedAsset> {
  const url = URL.createObjectURL(blob);
  const el = await loadImage(url);
  const asset: LoadedAsset = {
    id,
    url,
    el,
    width: el.naturalWidth,
    height: el.naturalHeight,
  };
  cache.set(id, asset);
  return asset;
}

export interface AddResult {
  metas: AssetMeta[];
  failed: string[];
}

export async function addFiles(files: File[]): Promise<AddResult> {
  const metas: AssetMeta[] = [];
  const failed: string[] = [];

  for (const file of files) {
    if (!file.type.startsWith('image/')) {
      failed.push(file.name);
      continue;
    }
    const id = uid('img');
    try {
      const asset = await register(id, file);
      await blobStore.put(id, file).catch(() => undefined);
      metas.push({
        id,
        name: file.name,
        width: asset.width,
        height: asset.height,
        type: file.type,
        size: file.size,
        addedAt: Date.now(),
      });
    } catch {
      failed.push(file.name);
    }
  }

  if (metas.length) emit();
  return { metas, failed };
}

/** Re-hydrate previously saved photos on startup. */
export async function restoreAssets(ids: ID[]): Promise<ID[]> {
  const missing: ID[] = [];
  for (const id of ids) {
    if (cache.has(id)) continue;
    try {
      const blob = await blobStore.get(id);
      if (!blob) {
        missing.push(id);
        continue;
      }
      await register(id, blob);
    } catch {
      missing.push(id);
    }
  }
  emit();
  return missing;
}

export function removeAsset(id: ID) {
  const asset = cache.get(id);
  if (asset) URL.revokeObjectURL(asset.url);
  cache.delete(id);
  blobStore.delete(id).catch(() => undefined);
  emit();
}

export function clearAssets() {
  cache.forEach((a) => URL.revokeObjectURL(a.url));
  cache.clear();
  blobStore.clear().catch(() => undefined);
  emit();
}

/** Register an already-decoded blob under a known id (project import / undo). */
export async function addBlob(id: ID, blob: Blob) {
  if (cache.has(id)) return cache.get(id)!;
  const asset = await register(id, blob);
  await blobStore.put(id, blob).catch(() => undefined);
  emit();
  return asset;
}

export function blobFor(id: ID) {
  return blobStore.get(id);
}
